import React, { useEffect, useRef, useState } from "react";
import {
  dashboard_invoice_green_tick,
  dashboard_invoice_order_date,
  dashboard_invoice_order_history,
  merchant_dashboard_location_icon,
  pdf,
  manage_my_info_edit_logo,
} from "../../../assets/images";
import styled from "styled-components";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"; // Import useQueryClient
import axiosInstance from "../../../services/axios.instance";
import { API_URL } from "../../../services/apiConfig";
import { useForm } from "react-hook-form";
import ErrorModal from "../../ErrorModal";
import ConfirmationModal from "../../ConfirmationModal";
import { useLocation, useNavigate } from "react-router-dom";
import ImagePopupModal from "../ImagePopupModal";
import CustomInput from "../../CustomInput";
import { toast } from "react-toastify";
import CustomRadio from "../../customRadio";
import { OverlayTrigger, Popover } from "react-bootstrap";

const VdrOrderEdit = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const OrderId = new URLSearchParams(location.search).get("OrderId");
  const queryClient = useQueryClient(); // Initialize queryClient

  const {
    data: invoiceDetails,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["invoiceDetails", OrderId],
    queryFn: async () => {
      const response = await axiosInstance.get(
        `${API_URL.ORDER_DETAILS}/${OrderId}/details`,
        {
          headers: {
            location: "0,0",
            language: "english",
          },
        }
      );
      return response.data;
    },
  });

  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [daysNumber, setDaysNumber] = useState("");
  const [recollectedFuel, setRecollectedFuel] = useState("");
  const [deliveryBoyName, setDeliveryBoyName] = useState("");
  const [deliveryBoyPhone, setDeliveryBoyPhone] = useState("");
  const [comments, setComments] = useState("");
  const {
    control,
    formState: { isValid },
  } = useForm({ mode: "onChange" });

  const [dateTime, setDateTime] = useState("");
  const [minDateTime, setMinDateTime] = useState("");
  const [recollectionDateTime, setRecollectionDateTime] = useState("");
  const [enableRecollectionRequest, setEnableRecollectionChangeRequest] =
    useState(false);

  const [showConfirmationModal, setShowConfirmationModal] = useState(false); // State for confirmation modal

  // New states for editable image URLs
  const [editableCheckListImageUrls, setEditableCheckListImageUrls] = useState<
    string[]
  >(["", ""]);
  const [
    editableRecollectedCheckListImageUrls,
    setEditableRecollectedCheckListImageUrls,
  ] = useState<string[]>([]);
  const [
    editableAdditionalCheckListImageUrls,
    setEditableAdditionalCheckListImageUrls,
  ] = useState<string[]>([]);

  useEffect(() => {
    const now = new Date();
    const isoString = now.toISOString().substring(0, 16);
    setMinDateTime(isoString);
  }, []);

  useEffect(() => {
    if (invoiceDetails) {
      if (invoiceDetails?.recollection_date) {
        setRecollectionDateTime(invoiceDetails?.recollection_date);
      }
      // Initialize editable image URLs from fetched data only if they haven't been set yet
      if (!editableCheckListImageUrls[0] && !editableCheckListImageUrls[1]) {
        const initialUrls = invoiceDetails?.products[0]?.checkListImageUrls || [
          "",
          "",
        ];
        setEditableCheckListImageUrls(initialUrls.slice(0, 2));
      }
      if (
        !editableRecollectedCheckListImageUrls[0] &&
        !editableRecollectedCheckListImageUrls[1]
      ) {
        setEditableRecollectedCheckListImageUrls(
          invoiceDetails?.products[0]?.recollectedCheckListImageUrls || []
        );
      }
      if (!editableAdditionalCheckListImageUrls.length) {
        setEditableAdditionalCheckListImageUrls(
          invoiceDetails?.products[0]?.additionalCheckListImageUrls || []
        );
      }
    }
  }, [invoiceDetails]);

  const formatDate = (isoDate: any) => {
    if (!isoDate) return "";
    const date = new Date(isoDate);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${day}-${month}-${year}`;
  };

  const [showImagePopupModal, setShowImagePopupModal] = useState(false);
  const [imageSource, setImageSource] = useState("");

  const [selectedOptions, setSelectedOptions] = useState<string>("");
  const handleRadioChange = (value: string) => {
    setSelectedOptions(value);
  };

  const isButtonDisabled = (): boolean => {
    // Current logic for other button disablement
    if (!isValid) {
      return true;
    }
    if (selectedOptions === "EXTENDED") {
      return !daysNumber;
    }
    if (selectedOptions === "RECOLLECTION") {
      if (invoiceDetails?.delivered_by !== "CUSTOMER") {
        return (
          !deliveryBoyName ||
          !deliveryBoyPhone ||
          !dateTime ||
          enableRecollectionRequest
        );
      }
      return !dateTime || enableRecollectionRequest;
    }

    // Disable update button if no changes were made to image URLs
    const originalCheckListUrls =
      invoiceDetails?.products[0]?.checkListImageUrls || [];
    const originalRecollectedUrls =
      invoiceDetails?.products[0]?.recollectedCheckListImageUrls || [];
    const originalAdditionalUrls =
      invoiceDetails?.products[0]?.additionalCheckListImageUrls || [];

    const additionalUrlsChanged =
      JSON.stringify(editableAdditionalCheckListImageUrls.sort()) !==
      JSON.stringify(originalAdditionalUrls.sort());

    const checkListUrlsChanged =
      JSON.stringify(editableCheckListImageUrls.sort()) !==
      JSON.stringify(originalCheckListUrls.sort());
    const recollectedUrlsChanged =
      JSON.stringify(editableRecollectedCheckListImageUrls.sort()) !==
      JSON.stringify(originalRecollectedUrls.sort());

    return !(
      checkListUrlsChanged ||
      recollectedUrlsChanged ||
      additionalUrlsChanged
    );
  };
  // add near other states
  const [currentUploadIndex, setCurrentUploadIndex] = useState<number | null>(
    null
  );

  // State to track which document is being uploaded
  const [currentUploadTarget, setCurrentUploadTarget] = useState<
    "vdr" | "rental" | "recollected" | "additional" | null
  >(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const openFileInput = (
    target: "vdr" | "rental" | "recollected" | "additional",
    index?: number
  ) => {
    setCurrentUploadTarget(target);
    setCurrentUploadIndex(typeof index === "number" ? index : null);

    // reset value so selecting same file again triggers onChange
    if (fileInputRef.current) fileInputRef.current.value = "";
    fileInputRef?.current?.click();
  };

  const handleImageUpload = async (files: FileList | null) => {
    if (!files || !currentUploadTarget) return;
    const file = files[0];
    if (!file) return;

    // determine the target index to write into
    let targetIndex = 0;
    if (currentUploadTarget === "vdr") targetIndex = 0;
    else if (currentUploadTarget === "rental") targetIndex = 1;
    else if (
      currentUploadTarget === "recollected" ||
      currentUploadTarget === "additional"
    ) {
      targetIndex =
        typeof currentUploadIndex === "number" ? currentUploadIndex : 0;
    }

    try {
      // show loading placeholder using functional updates (prevents stale state)
      if (currentUploadTarget === "vdr" || currentUploadTarget === "rental") {
        setEditableCheckListImageUrls((prev = ["", ""]) => {
          const arr = [...prev];
          while (arr.length < 2) arr.push("");
          arr[targetIndex] = "loading";
          return arr;
        });
      } else if (currentUploadTarget === "recollected") {
        setEditableRecollectedCheckListImageUrls((prev = []) => {
          const arr = [...prev];
          while (arr.length <= targetIndex) arr.push("");
          arr[targetIndex] = "loading";
          return arr;
        });
      } else if (currentUploadTarget === "additional") {
        setEditableAdditionalCheckListImageUrls((prev = []) => {
          const arr = [...prev];
          while (arr.length <= targetIndex) arr.push("");
          arr[targetIndex] = "loading";
          return arr;
        });
      }

      // upload
      const formData = new FormData();
      formData.append("product_image", file);
      const response = await axiosInstance.post(
        API_URL.PRODUCT_IMAGE_UPLOAD,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      const uploadedUrl = response.data;

      // write uploaded URL back to the correct index
      if (currentUploadTarget === "vdr" || currentUploadTarget === "rental") {
        setEditableCheckListImageUrls((prev = ["", ""]) => {
          const arr = [...prev];
          while (arr.length < 2) arr.push("");
          arr[targetIndex] = uploadedUrl;
          return arr;
        });
      } else if (currentUploadTarget === "recollected") {
        setEditableRecollectedCheckListImageUrls((prev = []) => {
          const arr = [...prev];
          while (arr.length <= targetIndex) arr.push("");
          arr[targetIndex] = uploadedUrl;
          return arr;
        });
      } else if (currentUploadTarget === "additional") {
        setEditableAdditionalCheckListImageUrls((prev = []) => {
          const arr = [...prev];
          while (arr.length <= targetIndex) arr.push("");
          arr[targetIndex] = uploadedUrl;
          return arr;
        });
      }

      // reset file input
      if (fileInputRef.current) fileInputRef.current.value = "";

      toast.success("Uploaded successfully!");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload. Please try again.");
    } finally {
      setCurrentUploadTarget(null);
      setCurrentUploadIndex(null);
    }
  };

  const onClickVdrDocument = (imageUrl: string) => {
    setImageSource(imageUrl);
    setShowImagePopupModal(true);
  };

  const refpopup = useRef(null);

  const popoverHoverFocus = (
    <Popover id="popover-trigger-hover-focus" className="popover-custom-edit">
      <Popover.Body className="d-flex align-items-center">
        <span className="invoice-div-data text-light">
          Edited by Kirkos backoffice
        </span>
      </Popover.Body>
    </Popover>
  );

  // Mutation for updating rental documents
  const updateRentalDocumentsMutation = useMutation({
    mutationFn: async (data: {
      order_number: string;
      check_list_image_urls: string[];
      recollected_check_list_image_urls: string[];
      additional_check_list_image_urls: string[];
    }) => {
      const response = await axiosInstance.put(
        API_URL.UPDATE_RENTAL_DOCUMENTS,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      toast.success("Rental documents updated successfully!");
      queryClient.invalidateQueries({ queryKey: ["invoiceDetails", OrderId] }); // Invalidate and refetch invoice details
      setShowConfirmationModal(false); // Close confirmation modal
    },
    onError: (error) => {
      setErrorMessage("Failed to update rental documents. Please try again.");
      setShowErrorModal(true);
      console.error("Error updating rental documents:", error);
    },
  });

  const handleUpdate = () => {
    setShowConfirmationModal(true); // Show confirmation modal before updating
  };

  const confirmUpdate = () => {
    if (invoiceDetails?.orderId) {
      updateRentalDocumentsMutation.mutate({
        order_number: invoiceDetails.orderId,
        check_list_image_urls: editableCheckListImageUrls,
        recollected_check_list_image_urls:
          editableRecollectedCheckListImageUrls,
        additional_check_list_image_urls: editableAdditionalCheckListImageUrls,
      });
    }
  };

  if (isLoading) {
    return <div className="text-center pt-5">Loading order details...</div>;
  }

  if (isError) {
    return (
      <div className="text-center pt-5 text-danger">
        Error loading order details.
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col-md-12 text-start div-bg-invoice-details">
        <div className="card card-bg-invoice-details" ref={refpopup}>
          <div className="col-12 text-center py-4">
            <h6 className="fw-bold mb-0">VDR EDIT</h6>
          </div>
          <div className="card-body px-5">
            <div className="row pt-4 pb-3">
              <div className="col-md-6">
                <table className="invoice-table-width">
                  <tr className="invoice-table-tr-first">
                    <td className="invoice-table-td">Purchase order #</td>
                    <td>Order Created At</td>
                  </tr>
                  <tr>
                    <td className="invoice-text-yellow">
                      {invoiceDetails?.orderId}
                    </td>
                    <td>{formatDate(invoiceDetails?.createdAt)}</td>
                  </tr>
                </table>
              </div>

              <div className="col-12 pt-3">
                <div className="row bg-delivery-details pb-3">
                  <div className="col-md-12 invoice-div-head pt-3 fs-6 invoice-text-yellow">
                    Delivery Details
                  </div>
                  <div className="col-md-6">
                    <div className="col-md-12 invoice-div-head pt-3">
                      Plate Number
                    </div>
                    <div className="col-md-12 invoice-div-data">
                      {invoiceDetails?.products[0].plateNumber}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="col-md-12 invoice-div-head pt-3">
                      Delivery Executive Name
                    </div>
                    <div className="col-md-12 invoice-div-data">
                      {invoiceDetails?.delivery_boy_details?.firstName}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="col-md-12 invoice-div-head pt-3">
                      Delivery Executive Phone No
                    </div>
                    <div className="col-md-12 invoice-div-data">
                      +971 {invoiceDetails?.delivery_boy_details?.phoneNumber}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="col-md-12 invoice-div-head pt-3">
                      Delivery Date and Time
                    </div>
                    <div className="col-md-12 invoice-div-data">
                      {new Date(
                        invoiceDetails?.delivery_date
                      ).toLocaleDateString("en-GB")}{" "}
                      at{" "}
                      {new Date(
                        invoiceDetails?.delivery_date
                      ).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                        hourCycle: "h23",
                      })}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-6 pt-5 align-content-center">
                <div className="col-md-12 radio_button_text-order pt-3">
                  Documents (VDR & Rental Agreement)
                </div>
                <div className="row pt-3">
                  {/* VDR Document */}
                  <div className="col-6 position-relative mb-3">
                    {/* <div className="fw-bold mb-2">VDR Document</div> */}
                    <div>
                      {editableCheckListImageUrls?.[0] && (
                        <div className="mb-2">
                          {editableCheckListImageUrls[0].endsWith(".pdf") ? (
                            <a
                              href={editableCheckListImageUrls[0]}
                              download="VDR.pdf"
                              style={{ display: "block", margin: "10px 0" }}
                            >
                              <img
                                src={pdf}
                                alt="PDF"
                                style={{ width: 75, height: 75 }}
                              />
                              <div>View VDR</div>
                            </a>
                          ) : (
                            <div>
                              <img
                                src={editableCheckListImageUrls[0]}
                                onClick={() =>
                                  onClickVdrDocument(
                                    editableCheckListImageUrls[0]
                                  )
                                }
                                alt="Document"
                                style={{
                                  width: 75,
                                  height: 75,
                                  cursor: "pointer",
                                }}
                              />
                              <a
                                href={editableCheckListImageUrls[0]}
                                download="Document"
                                style={{
                                  display: "block",
                                  marginTop: "5px",
                                  fontSize: "14px",
                                }}
                              >
                                Download
                              </a>
                            </div>
                          )}
                        </div>
                      )}
                      <div className="d-flex align-items-center">
                        <button
                          className="btn-custom-upload"
                          onClick={() => openFileInput("vdr")}
                        >
                          Browse File
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Rental Agreement */}
                  <div className="col-6 position-relative mb-3">
                    {/* <div className="fw-bold mb-2">Rental Agreement</div> */}
                    <div>
                      {editableCheckListImageUrls?.[1] && (
                        <div className="mb-2">
                          {editableCheckListImageUrls[1].endsWith(".pdf") ? (
                            <a
                              href={editableCheckListImageUrls[1]}
                              download="Rental_Agreement.pdf"
                              style={{ display: "block", margin: "10px 0" }}
                            >
                              <img
                                src={pdf}
                                alt="PDF"
                                style={{ width: 75, height: 75 }}
                              />
                              <div>View Agreement</div>
                            </a>
                          ) : (
                            <div>
                              <img
                                src={editableCheckListImageUrls[1]}
                                onClick={() =>
                                  onClickVdrDocument(
                                    editableCheckListImageUrls[1]
                                  )
                                }
                                alt="Document"
                                style={{
                                  width: 75,
                                  height: 75,
                                  cursor: "pointer",
                                }}
                              />
                              <a
                                href={editableCheckListImageUrls[1]}
                                download="Document"
                                style={{
                                  display: "block",
                                  marginTop: "5px",
                                  fontSize: "14px",
                                }}
                              >
                                Download
                              </a>
                            </div>
                          )}
                        </div>
                      )}
                      <div className="d-flex align-items-center">
                        <button
                          className="btn-custom-upload"
                          onClick={() => openFileInput("rental")}
                        >
                          Browse File
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Additional Documents */}
                <div className="col-md-12 pt-4">
                  <div className="col-md-12 radio_button_text-order pt-3">
                    Additional Documents
                  </div>
                  <div className="row pt-3">
                    {editableAdditionalCheckListImageUrls?.length > 0 ? (
                      // Show uploaded files and one browse button if only one file is uploaded
                      <>
                        {editableAdditionalCheckListImageUrls.map(
                          (url, index) => (
                            <div
                              key={index}
                              className="col-6 position-relative mb-3"
                            >
                              {url && (
                                <div className="mb-2">
                                  {url.endsWith(".pdf") ? (
                                    <a
                                      href={url}
                                      download={`Additional_${index + 1}.pdf`}
                                      style={{
                                        display: "block",
                                        margin: "10px 0",
                                      }}
                                    >
                                      <img
                                        src={pdf}
                                        alt="PDF"
                                        style={{ width: 75, height: 75 }}
                                      />
                                      <div>View Additional Document</div>
                                    </a>
                                  ) : (
                                    <div>
                                      <img
                                        src={url}
                                        onClick={() => onClickVdrDocument(url)}
                                        alt="Additional Document"
                                        style={{
                                          width: 75,
                                          height: 75,
                                          cursor: "pointer",
                                        }}
                                      />
                                      <a
                                        href={url}
                                        download={`Additional_${index + 1}`}
                                        style={{
                                          display: "block",
                                          marginTop: "5px",
                                          fontSize: "14px",
                                        }}
                                      >
                                        Download
                                      </a>
                                    </div>
                                  )}
                                </div>
                              )}
                              <div className="d-flex align-items-center">
                                <button
                                  className="btn-custom-upload"
                                  onClick={() =>
                                    openFileInput("additional", index)
                                  }
                                >
                                  Browse File
                                </button>
                              </div>
                            </div>
                          )
                        )}
                        {editableAdditionalCheckListImageUrls.length === 1 && (
                          <div className="col-6 position-relative mb-3">
                            <button
                              className="btn-custom-upload"
                              onClick={() => openFileInput("additional", 1)}
                            >
                              Browse File
                            </button>
                          </div>
                        )}
                      </>
                    ) : (
                      // Show two browse buttons when no files are uploaded
                      <>
                        <div className="col-6 position-relative mb-3">
                          <button
                            className="btn-custom-upload"
                            onClick={() => openFileInput("additional", 0)}
                          >
                            Browse File
                          </button>
                        </div>
                        <div className="col-6 position-relative mb-3">
                          <button
                            className="btn-custom-upload"
                            onClick={() => openFileInput("additional", 1)}
                          >
                            Browse File
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                {invoiceDetails?.orderStatus === "RECOLLECTED" && (
                  <>
                    <div className="col-md-12 invoice-div-data pt-3">
                      Recollection (VDR)
                    </div>
                    <div className="row">
                      <div className="col-6 position-relative mb-3">
                        <div>
                          {editableRecollectedCheckListImageUrls?.[0] && (
                            <div className="mb-2">
                              {editableRecollectedCheckListImageUrls[0].endsWith(
                                ".pdf"
                              ) ? (
                                <a
                                  href={
                                    editableRecollectedCheckListImageUrls[0]
                                  }
                                  download="Recollection_VDR.pdf"
                                  style={{ display: "block", margin: "10px 0" }}
                                >
                                  <img
                                    src={pdf}
                                    alt="PDF"
                                    style={{ width: 75, height: 75 }}
                                  />
                                  <div>View Recollection VDR</div>
                                </a>
                              ) : (
                                <div>
                                  <img
                                    src={
                                      editableRecollectedCheckListImageUrls[0]
                                    }
                                    onClick={() =>
                                      onClickVdrDocument(
                                        editableRecollectedCheckListImageUrls[0]
                                      )
                                    }
                                    alt="Recollection VDR"
                                    style={{
                                      width: 75,
                                      height: 75,
                                      cursor: "pointer",
                                    }}
                                  />
                                  <a
                                    href={
                                      editableRecollectedCheckListImageUrls[0]
                                    }
                                    download="Recollection_VDR"
                                    style={{
                                      display: "block",
                                      marginTop: "5px",
                                      fontSize: "14px",
                                    }}
                                  >
                                    Download
                                  </a>
                                </div>
                              )}
                            </div>
                          )}
                          <div className="d-flex align-items-center">
                            <button
                              className="btn-custom-upload"
                              onClick={() => openFileInput("recollected")}
                            >
                              Browse File
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div className="col-md-6 pt-5">
                <div className="card invoice-card-inner">
                  <div className="card-body">
                    <div className="row">
                      <div className="col-1 text-center">
                        <img
                          src={dashboard_invoice_order_history}
                          height={"16px"}
                          alt=""
                        />
                      </div>
                      <div className="col invoice-text-yellow">
                        Order History
                      </div>
                    </div>
                    <div className="col-md-11 pt-3">
                      <div className="d-flex ">
                        <div className="col-1 text-center">
                          <img
                            src={dashboard_invoice_order_date}
                            height={"18px"}
                            alt=""
                          />
                        </div>
                        <div className="col">
                          <div className="invoice-div-head">
                            Purchase order Date
                          </div>
                          <div className="invoice-inner-card-text">
                            {formatDate(invoiceDetails?.createdAt)}{" "}
                          </div>
                        </div>
                      </div>
                      <div className="d-flex ps-2">
                        <div className="col-1 text-center hor_line"></div>
                      </div>
                      <div className="d-flex pt-2">
                        <div className="col-1 text-center">
                          <img
                            src={dashboard_invoice_green_tick}
                            height={"18px"}
                            alt=""
                          />
                        </div>
                        <div className="col">
                          <div className="invoice-div-head">Status</div>
                          <div className="invoice-text-green">
                            {invoiceDetails?.orderStatus}
                          </div>
                        </div>
                      </div>
                      <div className="d-flex ps-2">
                        <div className="col-1 text-center hor_line"></div>
                      </div>
                      <div className="d-flex pt-2">
                        <div className="col-1 text-center">
                          <img
                            src={merchant_dashboard_location_icon}
                            height={"18px"}
                            alt=""
                          />
                        </div>
                        <div className="col">
                          <div
                            className={`col-md-12 invoice-div-head d-flex align-items-center ${
                              invoiceDetails?.latest_modified_field_flags
                                ?.delivery_location ||
                              invoiceDetails?.latest_modified_field_flags
                                ?.delivery_date
                                ? "text-danger"
                                : ""
                            }`}
                          >
                            Delivery Date & Location
                            {(invoiceDetails?.modified_field_flags
                              ?.delivery_location ||
                              invoiceDetails?.modified_field_flags
                                ?.delivery_date) && (
                              <OverlayTrigger
                                container={refpopup}
                                trigger={["hover"]}
                                placement="top"
                                overlay={popoverHoverFocus}
                              >
                                <div className="ps-2">
                                  <img
                                    src={manage_my_info_edit_logo}
                                    alt="Edit"
                                    width="18"
                                  />
                                </div>
                              </OverlayTrigger>
                            )}
                          </div>
                          <div className="invoice-inner-card-text">
                            {formatDate(invoiceDetails?.delivery_date)} |{" "}
                            {invoiceDetails?.delivery_location?.address}
                          </div>
                        </div>
                      </div>

                      <div className="col-md-11 pt-3">
                        {invoiceDetails?.order_reassign_history_response?.some(
                          (history: any) => history?.delivery_date
                        ) &&
                          invoiceDetails?.order_reassign_history_response?.some(
                            (history: any) =>
                              history?.previous_delivery_date !==
                              history?.delivery_date
                          ) && (
                            <>
                              <div className="d-flex">
                                <div className="col-1 text-center">
                                  <img
                                    src={dashboard_invoice_order_date}
                                    height={"18px"}
                                    alt=""
                                  />
                                </div>
                                <div className="col">
                                  <div className="invoice-div-head">
                                    Initial Delivery Date
                                  </div>
                                  <div className="invoice-inner-card-text">
                                    {formatDate(
                                      invoiceDetails?.order_reassign_history_response?.find(
                                        (history: any) =>
                                          history?.previous_delivery_date &&
                                          history?.previous_delivery_date !==
                                            history?.delivery_date
                                      )?.previous_delivery_date || ""
                                    )}
                                  </div>
                                </div>
                              </div>

                              <div className="d-flex ps-2">
                                <div className="col-1 text-center hor_line"></div>
                              </div>

                              {invoiceDetails?.order_reassign_history_response?.map(
                                (history: any, index: number) =>
                                  history?.delivery_date &&
                                  history?.previous_delivery_date !==
                                    history?.delivery_date && (
                                    <div key={index}>
                                      <div className="d-flex pt-2">
                                        <div className="col-1 text-center">
                                          <img
                                            src={dashboard_invoice_order_date}
                                            height={"18px"}
                                            alt=""
                                          />
                                        </div>
                                        <div className="col">
                                          <div className="invoice-div-head">
                                            Updated Delivery Date
                                          </div>
                                          <div className="invoice-inner-card-text">
                                            {formatDate(history?.delivery_date)}
                                          </div>
                                        </div>
                                      </div>

                                      <div className="d-flex ps-2">
                                        <div className="col-1 text-center hor_line"></div>
                                      </div>
                                    </div>
                                  )
                              )}
                            </>
                          )}
                      </div>

                      <div className="d-flex ps-2">
                        <div className="col-1 text-center hor_line"></div>
                      </div>
                      <div className="d-flex pt-2">
                        <div className="col-1 text-center">
                          <img
                            src={merchant_dashboard_location_icon}
                            height={"18px"}
                            alt=""
                          />
                        </div>
                        <div className="col">
                          <div
                            className={`col-md-12 invoice-div-head d-flex align-items-center ${
                              invoiceDetails?.latest_modified_field_flags
                                ?.recollection_location ||
                              invoiceDetails?.latest_modified_field_flags
                                ?.recollection_date
                                ? "text-danger"
                                : ""
                            }`}
                          >
                            Recollection Date & Location
                            {(invoiceDetails?.modified_field_flags
                              ?.recollection_location ||
                              invoiceDetails?.modified_field_flags
                                ?.recollection_date) && (
                              <OverlayTrigger
                                container={refpopup}
                                trigger={["hover"]}
                                placement="top"
                                overlay={popoverHoverFocus}
                              >
                                <div className="ps-2">
                                  <img
                                    src={manage_my_info_edit_logo}
                                    alt="Edit"
                                    width="18"
                                  />
                                </div>
                              </OverlayTrigger>
                            )}
                          </div>
                          <div className="invoice-inner-card-text">
                            {formatDate(invoiceDetails?.recollection_date)} |{" "}
                            {invoiceDetails?.recollection_location?.address}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Hidden file input */}
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={(e) => handleImageUpload(e.target.files)}
              accept="image/*,.pdf"
            />

            <div className="col-md-12 d-flex justify-content-end pt-4">
              <button
                className="btn btn-primary"
                onClick={handleUpdate}
                disabled={
                  isButtonDisabled() || updateRentalDocumentsMutation.isPending
                }
              >
                {updateRentalDocumentsMutation.isPending
                  ? "Updating..."
                  : "Update"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        message={errorMessage}
      />

      <ImagePopupModal
        isOpen={showImagePopupModal}
        src={imageSource}
        onClose={() => setShowImagePopupModal(false)}
        message={""}
      />

      <ConfirmationModal
        isOpen={showConfirmationModal}
        onClose={() => setShowConfirmationModal(false)}
        onConfirm={confirmUpdate}
        message="Are you sure you want to update the rental documents?"
      />
    </div>
  );
};

export default VdrOrderEdit;
